import { JsonRpcSigner, Web3Provider } from "@ethersproject/providers";
import { useWeb3React } from "@web3-react/core";
import { BigNumber, Contract, ethers } from "ethers";
import { useEffect, useMemo, useState } from "react";
import { ADDRESS_ZERO, CHAIN_ID, DECIMALS, TOKENS, TOKEN_ADDRESSES } from "../constants";
import erc20Abi from "../data/erc20.json";

declare const window: any;

export function getProviderName(providerConnection?: string) {
  if (providerConnection === "metamask") {
    return "Metamask";
  }

  if (providerConnection === "eip-1193:") {
    return "WalletConnect";
  }

  return "Unknown";
}

export const networkConfigs = {
  1: {
    chainId: `0x${Number(1).toString(16)}`,
    chainName: "Ethereum Mainnet",
    nativeCurrency: {
      name: "ETH",
      symbol: "eth",
      decimals: 18,
    },
    rpcUrls: [`${process.env.REACT_APP_ETHEREUM_NODE_URI}`],
  },
  4: {
    chainId: `0x${Number(4).toString(16)}`,
    chainName: "Rinkeby Test Network",
    nativeCurrency: {
      name: "ETH",
      symbol: "eth",
      decimals: 18,
    },
    rpcUrls: [`${process.env.REACT_APP_RINKEBY_NODE_URI}`],
  },
};

export const setupNetwork = async (chainId: 1 | 4) => {
  const provider = window.ethereum;
  if (provider) {
    try {
      const currentChainId = await provider.request({ method: "net_version" });
      if (parseInt(currentChainId, 10) === CHAIN_ID) {
        return true;
      }
      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: `0x${chainId.toString(16)}` }],
      });
      return true;
    } catch (switchError: any) {
      console.warn("Can't switch to Supported network", switchError);
      if (switchError.code === 4902) {
        try {
          await provider.request({
            method: "wallet_addEthereumChain",
            params: [networkConfigs[chainId]],
          });
          return true;
        } catch (addError) {
          console.warn("Error adding Supported network", addError);
          return false;
        }
      }
      return false;
    }
  } else {
    console.warn("Can't setup the network on metamask because window.ethereum is undefined");
    window.open(`https://metamask.app.link/dapp/${window.location.href.replace(/^https?:\/\//, "")}`);
    return false;
  }
};

export function getSigner(library: Web3Provider, account: string): JsonRpcSigner {
  return library.getSigner(account).connectUnchecked();
}

export function getProviderOrSigner(library: Web3Provider, account?: string): Web3Provider | JsonRpcSigner {
  return account ? getSigner(library, account) : library;
}

export function isAddress(value: any): string | false {
  try {
    return ethers.utils.getAddress(value);
  } catch {
    return false;
  }
}

export function getContract(address: string, ABI: any, library: Web3Provider, account?: string): Contract {
  if (!isAddress(address) || address === ADDRESS_ZERO) {
    throw Error(`Invalid 'address' parameter '${address}'.`);
  }

  return new Contract(address, ABI, getProviderOrSigner(library, account) as any);
}

export function useContract(address: string | undefined, ABI: any, withSignerIfPossible = true): Contract | null {
  const { library, account } = useWeb3React();

  return useMemo(() => {
    if (!address || !ABI || !library) return null;
    try {
      return getContract(address, ABI, library, withSignerIfPossible && account ? account : undefined);
    } catch (error) {
      console.warn("Failed to get contract", error);
      return null;
    }
  }, [address, ABI, library, withSignerIfPossible, account]);
}

export function useBalance(token: string): number {
  const { library, account } = useWeb3React();

  const [balance, setBalance] = useState(0);

  const tokenContract = useContract(TOKEN_ADDRESSES[token], erc20Abi, true);

  useEffect(() => {
    async function getBalance() {
      if (!account) {
        setBalance(0);
        return;
      }

      if (token === TOKENS.ETH) {
        const fetchedBalance = (await library.getBalance(account)) as BigNumber;
        setBalance(Number(ethers.utils.formatUnits(fetchedBalance, DECIMALS.ETH)));
      } else if (tokenContract) {
        const fetchedBalance = (await tokenContract.balanceOf(account)) as BigNumber;
        setBalance(Number(ethers.utils.formatUnits(fetchedBalance, DECIMALS.USDT)));
      } else {
        setBalance(0);
      }
    }

    getBalance();
  }, [account, library, token, tokenContract]);

  return balance;
}

export function floor(value: number, decimals: number): number {
  const precision = 10 ** decimals;
  return Math.floor(value * precision) / precision;
}
